import { UiFormField } from "../../utils/CommonTypes/UserTypes";
import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";

interface IRecaptchaProps {
  field?: UiFormField;
  fieldChanged: Function;
}

const SITE_KEY = "6LdIiq8UAAAAAFSXnLUAIcTsPeGH4UGuNIU3bvmk";

/**
 * Native grecaptcha wrapper (no react-recaptcha).
 * Renders into a fresh HTMLElement each effect cycle so React 18 StrictMode
 * remounts never hit "placeholder element must be an element or id" or
 * "already been rendered in this element".
 */
const MyCaptcha = (props: IRecaptchaProps) => {
  const [value, setValue] = useState<string>();
  const hostRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);

  useEffect(() => {
    pushToParent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    const host = hostRef.current;
    if (!host) return;

    // Fresh child each mount — grecaptcha refuses to re-use a previous host node.
    const target = document.createElement("div");
    host.innerHTML = "";
    host.appendChild(target);

    const onVerify = (token: string) => {
      const w: any = window;
      w.x_reCaptcha = token ?? "";
      setValue(token ?? "");
    };
    const onExpired = () => {
      const w: any = window;
      w.x_reCaptcha = "";
      setValue("");
    };

    const tryRender = (): boolean => {
      if (cancelled) return true;
      const w: any = window;
      if (!w.grecaptcha || typeof w.grecaptcha.render !== "function") return false;
      // ready() exists on modern api.js; fall back to render when missing.
      if (typeof w.grecaptcha.ready === "function") {
        w.grecaptcha.ready(() => {
          if (cancelled || widgetIdRef.current != null) return;
          try {
            widgetIdRef.current = w.grecaptcha.render(target, {
              sitekey: SITE_KEY,
              callback: onVerify,
              "expired-callback": onExpired,
            });
          } catch (e) {
            console.warn("reCAPTCHA render failed", e);
          }
        });
        return true;
      }
      try {
        widgetIdRef.current = w.grecaptcha.render(target, {
          sitekey: SITE_KEY,
          callback: onVerify,
          "expired-callback": onExpired,
        });
        return true;
      } catch (e) {
        console.warn("reCAPTCHA render failed", e);
        return false;
      }
    };

    if (!tryRender()) {
      pollTimer = setInterval(() => {
        if (tryRender() && pollTimer) {
          clearInterval(pollTimer);
          pollTimer = null;
        }
      }, 250);
    }

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
      try {
        const w: any = window;
        if (widgetIdRef.current != null && w.grecaptcha?.reset) {
          try {
            w.grecaptcha.reset(widgetIdRef.current);
          } catch {
            /* ignore */
          }
        }
      } catch {
        /* ignore */
      }
      widgetIdRef.current = null;
      if (hostRef.current) hostRef.current.innerHTML = "";
    };
  }, []);

  const pushToParent = () => {
    if (!props || !props.fieldChanged) {
      toast.error(`ReCaptcha missing key function.`, { autoClose: false });
      return;
    }
    props.fieldChanged(props.field?.Key, [value]);
  };

  return (
    <div>
      <div ref={hostRef} />
      <div className="show-on-has-error">
        {value && value.length > 0 && (
          <button type="button" className="btn btn-xs btn-default" onClick={pushToParent}>
            push
          </button>
        )}
      </div>
    </div>
  );
};

export default MyCaptcha;
